const intentInterface = {
    public_key: {
        title: 'View public key',
        description: 'Requests account public key. It\'s a simple way of authentication for Stellar-based applications. The response ensures that a user owns the corresponding secret key.',
        implicitFlow: false,
        params: {
            token: {
                description: 'Verification token generated by the application (should be unique or random).',
                type: 'string',
                required: false
            },
            callback: {
                description: 'Optional URL callback where Albedo will POST a signed token and public key.',
                type: 'string',
                required: false
            },
            require_existing: {
                description: 'Allow existing Albedo accounts only.',
                type: 'boolean',
                required: false
            }
        },
        returns: {
            pubkey: {
                description: 'User-selected public key.',
                type: 'string'
            },
            signed_message: {
                description: 'HEX-encoded authentication message derived from the public key and verification token.',
                type: 'string'
            },
            signature: {
                description: 'HEX-encoded ED25519 signature of the authentication message that can be further used to verify user\'s keypair ownership.',
                type: 'string'
            }
        }
    },
    sign_message: {
        title: 'Sign text message',
        description: 'Requests arbitrary message signing. Can be used to implement identity/ownership verification.',
        implicitFlow: true,
        params: {
            message: {
                description: 'Text message to sign.',
                type: 'string',
                required: true
            },
            pubkey: {
                description: 'Specific public key requested by the application.',
                type: 'string',
                required: false
            },
            callback: {
                description: 'Optional URL callback where Albedo will POST a signed message.',
                type: 'string',
                required: false
            }
        },
        returns: {
            pubkey: {
                description: 'User-selected public key.',
                type: 'string'
            },
            original_message: {
                description: 'Text message to sign from request.',
                type: 'string'
            },
            signed_message: {
                description: 'HEX-encoded message derived from the public key and original message.',
                type: 'string'
            },
            message_signature: {
                description: 'HEX-encoded ED25519 signature of the signed message.',
                type: 'string'
            }
        }
    },
    tx: {
        title: 'Sign transaction',
        description: 'Requests a signature for the transaction. Returns the signed transaction envelope that can be submitted to the network or used for multi-sig coordination.',
        implicitFlow: true,
        params: {
            xdr: {
                description: 'XDR-encoded transaction envelope to sign.',
                type: 'string',
                required: true
            },
            pubkey: {
                description: 'Specific public key requested by the application.',
                type: 'string',
                required: false
            },
            network: {
                description: 'Stellar network identifier.',
                type: 'string',
                required: false
            },
            callback: {
                description: 'Optional URL callback where Albedo will POST the signed transaction XDR instead of submitting it to Horizon.',
                type: 'string',
                required: false
            },
            description: {
                description: 'Optional human-friendly short transaction description provided by developers.',
                type: 'string',
                required: false
            },
            submit: {
                description: 'If set, the signed transaction will be submitted to the Horizon server instead of returning it to the application.',
                type: 'boolean',
                required: false
            }
        },
        returns: {
            xdr: {
                description: 'XDR-encoded transaction envelope from request.',
                type: 'string'
            },
            tx_hash: {
                description: 'HEX-encoded transaction hash.',
                type: 'string'
            },
            signed_envelope_xdr: {
                description: 'XDR-encoded transaction envelope with new signatures.',
                type: 'string'
            },
            network: {
                description: 'Stellar network identifier.',
                type: 'string'
            },
            result: {
                description: 'Optional response from Horizon if the transaction has been submitted automatically.',
                type: 'object'
            }
        }
    },
    pay: {
        title: 'Make payment',
        description: 'Requests a payment from a user. Works with any Stellar asset, supports transaction memo.',
        implicitFlow: true,
        params: {
            amount: {
                description: 'Requested payment amount.',
                type: 'string',
                required: true
            },
            destination: {
                description: 'Payment destination address.',
                type: 'string',
                required: true
            },
            asset_code: {
                description: 'Asset code (skip for XLM).',
                type: 'string',
                required: false
            },
            asset_issuer: {
                description: 'Asset issuer (skip for XLM).',
                type: 'string',
                required: false
            },
            memo: {
                description: 'Transaction memo (required for exchanges and some anchors).',
                type: 'string',
                required: false
            },
            memo_type: {
                description: 'Transaction memo type.',
                type: 'string',
                required: false
            },
            pubkey: {
                description: 'Specific public key requested by the application.',
                type: 'string',
                required: false
            },
            network: {
                description: 'Stellar network identifier or private network passphrase.',
                type: 'string',
                required: false
            },
            callback: {
                description: 'Optional URL callback where Albedo will POST the signed transaction XDR instead of submitting it to Horizon. ',
                type: 'string',
                required: false
            },
            submit: {
                description: 'If set, the signed transaction will be submitted to the Horizon server instead of returning it to the application.',
                type: 'boolean',
                required: false
            }
        },
        returns: {
            amount: {
                description: 'Payment amount from request.',
                type: 'string'
            },
            destination: {
                description: 'Payment destination address from request.',
                type: 'string'
            },
            asset_code: {
                description: 'Asset code from request.',
                type: 'string'
            },
            asset_issuer: {
                description: 'Asset issuer from request.',
                type: 'string'
            },
            memo: {
                description: 'Transaction memo from request.',
                type: 'string'
            },
            memo_type: {
                description: 'Transaction memo type from request.',
                type: 'string'
            },
            tx_hash: {
                description: 'HEX-encoded transaction hash.',
                type: 'string'
            },
            signed_envelope_xdr: {
                description: 'XDR-encoded transaction envelope with new signatures.',
                type: 'string'
            },
            pubkey: {
                description: 'User-selected public key.',
                type: 'string'
            },
            network: {
                description: 'Stellar network identifier.',
                type: 'string'
            },
            result: {
                description: 'Optional response from Horizon if the transaction has been submitted automatically.',
                type: 'object'
            }
        }
    },
    trust: {
        title: 'Establish trustline',
        description: 'Requests permission to create a trustline to a given Stellar asset. Gradually simplifies the process of creating trustlines for anchors, ICOs, and airdrops.',
        implicitFlow: true,
        params: {
            asset_code: {
                description: 'Trustline asset code.',
                type: 'string',
                required: true
            },
            asset_issuer: {
                description: 'Trustline asset issuer address.',
                type: 'string',
                required: true
            },
            limit: {
                description: 'Trust limit.',
                type: 'string',
                required: false
            },
            memo: {
                description: 'Transaction memo (required for exchanges and some anchors).',
                type: 'string',
                required: false
            },
            memo_type: {
                description: 'Transaction memo type.',
                type: 'string',
                required: false
            },
            pubkey: {
                description: 'Specific public key requested by the application.',
                type: 'string',
                required: false
            },
            network: {
                description: 'Stellar network identifier or private network passphrase.',
                type: 'string',
                required: false
            },
            callback: {
                description: 'Optional URL callback where Albedo will POST the signed transaction XDR instead of submitting it to Horizon. ',
                type: 'string',
                required: false
            },
            submit: {
                description: 'If set, the signed transaction will be submitted to the Horizon server instead of returning it to the application.',
                type: 'boolean',
                required: false
            }
        },
        returns: {
            asset_code: {
                description: 'Trustline asset code from request.',
                type: 'string'
            },
            asset_issuer: {
                description: 'Trustline asset issuer address from request.',
                type: 'string'
            },
            limit: {
                description: 'Trust limit from request.',
                type: 'string'
            },
            tx_hash: {
                description: 'HEX-encoded transaction hash.',
                type: 'string'
            },
            signed_envelope_xdr: {
                description: 'XDR-encoded transaction envelope with new signatures.',
                type: 'string'
            },
            pubkey: {
                description: 'User-selected public key.',
                type: 'string'
            },
            network: {
                description: 'Stellar network identifier.',
                type: 'string'
            },
            result: {
                description: 'Optional response from Horizon if the transaction has been submitted automatically.',
                type: 'object'
            }
        }
    },
    exchange: {
        title: 'Swap tokens',
        description: 'Requests permission to buy tokens on Stellar DEX at market price.',
        implicitFlow: false,
        params: {
            amount: {
                description: 'The amount of asset to buy.',
                type: 'string',
                required: true
            },
            max_price: {
                description: 'Maximum price the user willing to pay.',
                type: 'string',
                required: true
            },
            sell_asset_code: {
                description: 'Asset code of the asset to sell.',
                type: 'string',
                required: false
            },
            sell_asset_issuer: {
                description: 'Issuer account of the asset to sell.',
                type: 'string',
                required: false
            },
            buy_asset_code: {
                description: 'Asset code of the asset to buy.',
                type: 'string',
                required: false
            },
            buy_asset_issuer: {
                description: 'Issuer account of the asset to buy.',
                type: 'string',
                required: false
            },
            memo: {
                description: 'Transaction memo (required for exchanges and some anchors).',
                type: 'string',
                required: false
            },
            memo_type: {
                description: 'Transaction memo type.',
                type: 'string',
                required: false
            },
            pubkey: {
                description: 'Specific public key requested by the application.',
                type: 'string',
                required: false
            },
            network: {
                description: 'Stellar network identifier or private network passphrase.',
                type: 'string',
                required: false
            },
            callback: {
                description: 'Optional URL callback where Albedo will POST the signed transaction XDR instead of submitting it to Horizon.',
                type: 'string',
                required: false
            },
            submit: {
                description: 'If set, the signed transaction will be submitted to the Horizon server instead of returning it to the application.',
                type: 'boolean',
                required: false
            }
        },
        returns: {
            amount: {
                description: 'The amount of asset to buy from request.',
                type: 'string'
            },
            max_price: {
                description: 'Maximum price the user willing to pay from request.',
                type: 'string'
            },
            sell_asset_code: {
                description: 'Asset code of the asset to sell from request.',
                type: 'string'
            },
            sell_asset_issuer: {
                description: 'Issuer account of the asset to sell from request.',
                type: 'string'
            },
            buy_asset_code: {
                description: 'Asset code of the asset to buy from request.',
                type: 'string'
            },
            buy_asset_issuer: {
                description: 'Issuer account of the asset to buy from request.',
                type: 'string'
            },
            tx_hash: {
                description: 'HEX-encoded transaction hash.',
                type: 'string'
            },
            signed_envelope_xdr: {
                description: 'XDR-encoded transaction envelope with new signatures.',
                type: 'string'
            },
            pubkey: {
                description: 'User-selected public key.',
                type: 'string'
            },
            network: {
                description: 'Stellar network identifier.',
                type: 'string'
            },
            result: {
                description: 'Optional response from Horizon if the transaction has been submitted automatically.',
                type: 'object'
            }
        }
    },
    implicit_flow: {
        title: 'Implicit permissions',
        description: 'Requests temporary access token for one or more intents that can be used to execute actions without explicit confirmation from the user. In order to be executed implicitly, an implicit flow permissions for a given intent should be granted and "pubkey" parameter set.',
        implicitFlow: false,
        params: {
            intents: {
                description: 'Requested implicit flow intents.',
                type: 'string|string[]',
                required: true
            },
            network: {
                description: 'Stellar network identifier or private network passphrase.',
                type: 'string',
                required: false
            }
        },
        returns: {
            granted: {
                description: 'Whether a user granted permissions or not.',
                type: 'boolean'
            },
            intents: {
                description: 'Requested implicit flow intents.',
                type: 'string[]'
            },
            grants: {
                description: 'Implicit flow intents that have been granted.',
                type: 'string[]'
            },
            session: {
                description: 'Unique implicit session id.',
                type: 'string'
            },
            valid_until: {
                description: 'Session expiration timestamp.',
                type: 'number'
            },
            pubkey: {
                description: 'User-selected public key.',
                type: 'string'
            },
            network: {
                description: 'Stellar network identifier.',
                type: 'string'
            }
        }
    },
    manage_account: {
        title: 'Open account settings',
        description: 'Opens account settings window for a given account.',
        implicitFlow: false,
        params: {
            pubkey: {
                description: 'Specific public key requested by the application.',
                type: 'string',
                required: true
            },
            network: {
                description: 'Stellar network identifier or private network passphrase.',
                type: 'string',
                required: false
            }
        },
        returns: {
            pubkey: {
                description: 'Public key from intent request.',
                type: 'string'
            }
        }
    },
    batch: {
        title: 'Intents batch',
        description: 'Requests execution of several tx intents bundled together. This intent is atomic – a user confirms or rejects all bundled requests at once, with the same account and the same Stellar network.',
        implicitFlow: true,
        params: {
            intents: {
                description: 'Requested tx intents that should be executed together.',
                type: 'object[]',
                required: true
            },
            pubkey: {
                description: 'Specific public key requested by the application.',
                type: 'string',
                required: false
            },
            network: {
                description: 'Stellar network identifier or private network passphrase.',
                type: 'string',
                required: false
            }
        },
        returns: {
            intents: {
                description: 'Requested tx intents.',
                type: 'object[]'
            },
            results: {
                description: 'Array of results for each requested intent.',
                type: 'object[]'
            },
            pubkey: {
                description: 'User-selected public key.',
                type: 'string'
            },
            network: {
                description: 'Stellar network identifier.',
                type: 'string'
            }
        }
    }
}

export default intentInterface
